import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Select, Badge, Card } from "../components/ui";
import {
  STATO_META,
  TIPOLOGIA_RICHIESTA,
  statoMeta,
} from "../constants/richiesteIntervento";

const RUOLO_FORNITORE = 100000005;

function RichiesteListPage() {
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [richieste, setRichieste] = useState([]);
  const [rsaList, setRsaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ rsaId: "", stato: "" });

  const isFornitore = user?.ruolo === RUOLO_FORNITORE;

  useEffect(() => {
    if (isFornitore) return;
    apiRequest("/rsa")
      .then((res) => setRsaList(res?.value || []))
      .catch(() => {});
  }, [apiRequest, isFornitore]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.rsaId) params.set("rsaId", filters.rsaId);
      if (filters.stato) params.set("stato", filters.stato);
      const qs = params.toString();
      const res = await apiRequest(
        `/richieste-intervento${qs ? `?${qs}` : ""}`,
      );
      setRichieste(res?.value || []);
    } catch (err) {
      showToast(
        err.message || "Errore nel caricamento delle richieste",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [apiRequest, filters, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Richieste di Intervento
          </h1>
          <p className="text-gray-500 mt-1">
            Richieste tecniche on-demand con workflow di approvazione
          </p>
        </div>
        <Button onClick={() => navigate("/richieste-intervento/nuova")}>
          + Nuova richiesta
        </Button>
      </div>

      <Card className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!isFornitore && (
            <Select
              label="RSA"
              value={filters.rsaId}
              onChange={(e) =>
                setFilters((f) => ({ ...f, rsaId: e.target.value }))
              }
            >
              <option value="">Tutte</option>
              {rsaList.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome || r.name}
                </option>
              ))}
            </Select>
          )}
          <Select
            label="Stato"
            value={filters.stato}
            onChange={(e) =>
              setFilters((f) => ({ ...f, stato: e.target.value }))
            }
          >
            <option value="">Tutti</option>
            {Object.entries(STATO_META).map(([val, meta]) => (
              <option key={val} value={val}>
                {meta.label}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="p-6 text-gray-500">Caricamento…</div>
        ) : richieste.length === 0 ? (
          <div className="p-6 text-gray-500 text-center">
            Nessuna richiesta trovata.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-4 py-3">Oggetto</th>
                  <th className="px-4 py-3">RSA</th>
                  <th className="px-4 py-3">Fornitore</th>
                  <th className="px-4 py-3">Tipologia</th>
                  <th className="px-4 py-3">Data richiesta</th>
                  <th className="px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {richieste.map((r) => {
                  const meta = statoMeta(r.stato);
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {r.oggetto}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.rsa?.nome || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.fornitore?.nome || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {TIPOLOGIA_RICHIESTA[r.tipologiaRichiesta] || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {fmtDate(r.dataRichiestaEsecuzione)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/richieste-intervento/${r.id}`}
                          className="text-primary-600 hover:underline text-sm"
                        >
                          Dettaglio
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default RichiesteListPage;
